// The waiting room of « Jouer en ligne » (room_type "online"): players matched with strangers by the
// server. Unlike the friends' room (FriendsWaitingRoom) it has no code, no invite link, no bot stepper
// and no host crown. The table and its seats are WaitingTable's. The room starts by itself a few
// seconds after its last seat is taken (GameService.sync_online_autostart); the host may start at
// once, or fill the free seats with bots after a while. Like the friends' room, its top bar shows the
// room's state, not its name (owner's choice).
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Clock, Loader2 } from "lucide-react";
import { t, useLang } from "../../lib/i18n";
import {
  LaunchButton, LeaveButton, RulesChips, TableColorSection, Tips, WaitingTable, WaitingTitle, useSeatChime,
} from "./WaitingTable";

// The host may fill the free seats with bots once they have waited this long.
const BOT_OFFER_AFTER_S = 25;

function useNow(everyMs) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function OnlineWaitingRoom({ room, user, onLeave, onStart, onFillBots }) {
  useLang();
  const [enteredAt] = useState(Date.now);
  const [starting, setStarting] = useState(false);
  const [filling, setFilling] = useState(false);
  // When the room starts by itself: a local time, from the delay the server sends. Kept while
  // the broadcasts repeat the same countdown, so its bar never restarts.
  const [countdown, setCountdown] = useState(null); // { end, total } in ms
  useEffect(() => {
    const inS = room.autostart_in;
    setCountdown((c) => {
      if (inS == null) return null;
      const end = Date.now() + inS * 1000;
      return c && Math.abs(c.end - end) < 400 ? c : { end, total: inS * 1000 };
    });
  }, [room]);
  const now = useNow(countdown ? 200 : 1000);
  useSeatChime(room.members, room.num_players, user.user_id);

  const total = room.num_players;
  const seated = Math.min(room.members.length, total);
  const full = seated >= total;
  const isHost = room.host_user_id === user.user_id;
  const secsLeft = countdown ? Math.max(0, Math.ceil((countdown.end - now) / 1000)) : null;
  const waited = Math.max(0, Math.floor((now - enteredAt) / 1000));

  const start = () => {
    setStarting(true);
    Promise.resolve(onStart()).finally(() => setTimeout(() => setStarting(false), 1500));
  };
  const fill = () => {
    setFilling(true);
    Promise.resolve(onFillBots()).finally(() => setTimeout(() => setFilling(false), 1500));
  };

  const subline = !full
    ? t("waitingTable.missing", { n: total - seated })
    : countdown
    ? t("onlineRoom.startsSoon")
    : isHost
    ? t("waitingTable.hostStarts")
    : t("waitingTable.waitHost");

  let cta;
  if (!full) {
    cta = { busy: true, label: t("onlineRoom.cta.waiting", { a: seated, b: total }) };
  } else if (isHost) {
    cta = starting
      ? { busy: true, live: true, label: t("waitingTable.starting") }
      : { onClick: start, live: true, label: t("onlineRoom.cta.startNow") };
  } else {
    cta = {
      busy: true,
      live: true,
      label: secsLeft ? t("onlineRoom.cta.startsIn", { n: secsLeft }) : secsLeft === 0 ? t("waitingTable.starting") : t("room.waitingLaunch"),
    };
  }

  return (
    <div className="w-full max-w-lg flex flex-col gap-2">
      {/* ── The table ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rami-glass rounded-2xl border border-white/[0.08] p-3 sm:p-3.5 flex flex-col gap-2.5"
        data-testid="online-waiting-room"
      >
        <div className="flex items-center justify-between gap-2">
          <LeaveButton onClick={onLeave} />

          {/* The room's state, which changes once the table is full. */}
          <WaitingTitle
            title={full ? t("waitingTable.full") : t("onlineRoom.searching")}
            searching={!full}
            className="min-w-0 flex-1 text-center text-[17px] leading-tight"
          />

          <span
            title={t("onlineRoom.elapsed")}
            className="flex shrink-0 items-center gap-1 rounded-full border border-white/[0.08] bg-slate-900/60 px-2 py-1 text-[11px] font-semibold text-slate-300 tabular-nums"
          >
            <Clock className="h-3 w-3 text-slate-500" />
            <span dir="ltr">{clock(waited)}</span>
          </span>
        </div>

        <RulesChips room={room} />

        <p className="text-center text-[12px] text-slate-400">{subline}</p>

        <WaitingTable
          room={room}
          user={user}
          freeLabel={t("onlineRoom.seatSearching")}
          secsLeft={secsLeft}
          starting={secsLeft === 0}
        />

        {/* A spinner while seats are free; once full, the countdown's bar fills the button. */}
        <LaunchButton {...cta}>
          {countdown && (
            <motion.span
              key={countdown.end}
              aria-hidden="true"
              className={`absolute inset-0 origin-left rtl:origin-right ${cta.onClick ? "bg-amber-300/60" : "bg-amber-400/15"}`}
              initial={{ scaleX: Math.max(0, 1 - (countdown.end - Date.now()) / countdown.total) }}
              animate={{ scaleX: 1 }}
              transition={{ duration: Math.max(0, (countdown.end - Date.now()) / 1000), ease: "linear" }}
            />
          )}
        </LaunchButton>

        {/* Few players online: the host may fill the free seats with bots and play at once. */}
        <AnimatePresence>
          {isHost && !full && waited >= BOT_OFFER_AFTER_S && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-500/20 bg-emerald-950/30 px-3 py-1">
                <span className="text-[12px] leading-snug text-emerald-100/80" data-testid="fill-bots-note">
                  {/* With another player here the game counts for the rank (backend/ranking.py). */}
                  {room.members.filter((m) => !m.is_bot).length < 2 ? t("waitingTable.noRank") : t("onlineRoom.bots.ask")}
                </span>
                <button
                  type="button"
                  data-testid="fill-bots-btn"
                  onClick={fill}
                  disabled={filling}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-400 px-3 py-1 text-[12px] font-bold text-emerald-950 shadow-[0_2px_8px_rgba(52,211,153,0.35)] transition hover:bg-emerald-300 active:scale-95 disabled:opacity-60"
                >
                  {filling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                  {t("onlineRoom.bots.fill")}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── While waiting: a tip, the felt ────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.08 }}
        className="rami-glass rounded-2xl border border-white/[0.08] p-3 sm:p-3.5 flex flex-col gap-3"
      >
        <Tips room={room} />
        <TableColorSection />
      </motion.div>
    </div>
  );
}
